import type { ApexOptions } from 'apexcharts';
import { Option } from '@lib/interfaces/form';
import { getFormattedDate } from '@lib/utils/dateTimeHelpers';
import { dateFormatForApi } from '@lib/enums/dateTime';
import { getApexchartsLocales } from '@lib/utils/apexcharts';
import { createCustomTooltip } from '../MonitoringChartTooltip/MonitoringChartTooltip';
import { AnomaliesChartData, AxisSeries } from '../types';
import { STATIC_COLORS } from '../constants';

const STATIC_COLOR_VALUES = Object.values(STATIC_COLORS);

// Helper function to get static color for the entity by position
const getStaticColorForEntity = (_entityId: string, index: number): string => {
  // Use position-based color assignment (cycle through colors)
  return STATIC_COLOR_VALUES[index % STATIC_COLOR_VALUES.length];
};

// Helper function to create yaxis configuration
const createYAxisConfig = (tickAmount = 10, yLabel = '') => ({
  title: { text: yLabel },
  tickAmount,
  forceNiceScale: true,
  decimalsInFloat: 0,
  min: 0,
});

// Shared helper functions for scaling and date calculations
const DAY_MS = 24 * 60 * 60 * 1000;

// Helper: extract 'YYYY-MM-DD' from any ISO string.
const extractYmd = (value: string): string => String(value || '').slice(0, 10);
// Keep a fast path for YYYY-MM-DD when building series points
const ymdToUtcTs = (ymd: string): number => {
  const [y, m, d] = ymd.split('-').map((n) => Number(n));
  return Date.UTC(y, (m || 1) - 1, d || 1, 0, 0, 0, 0);
};
const toUtcStartOfDay = (value: string): number =>
  ymdToUtcTs(extractYmd(value));
const toUtcEndOfDay = (value: string): number =>
  ymdToUtcTs(extractYmd(value)) + (DAY_MS - 1);

const diffInMonths = (startTsVal: number, endTsVal: number) => {
  const sd = new Date(startTsVal);
  const ed = new Date(endTsVal);
  return (
    (ed.getUTCFullYear() - sd.getUTCFullYear()) * 12 +
    (ed.getUTCMonth() - sd.getUTCMonth())
  );
};

const diffInYears = (startTsVal: number, endTsVal: number) => {
  const sd = new Date(startTsVal);
  const ed = new Date(endTsVal);
  return ed.getUTCFullYear() - sd.getUTCFullYear();
};

const chooseScale = (
  spanDaysVal: number,
): 'day' | 'month' | 'months' | 'years' => {
  if (spanDaysVal <= 2) return 'day'; // 1-2 days use daily scale
  if (spanDaysVal <= 7) return 'day';
  if (spanDaysVal <= 45) return 'month';
  if (spanDaysVal <= 730) return 'months';
  return 'years';
};

const getTickAmount = (
  scale: ReturnType<typeof chooseScale>,
  spanDaysVal: number,
  minTs: number,
  maxTs: number,
): number => {
  if (scale === 'day') {
    if (spanDaysVal === 1) return 1; // single-day -> one label
    if (spanDaysVal === 2) return 2; // two days -> two labels
    return Math.min(spanDaysVal, 7); // longer small ranges up to 7 labels
  }
  if (scale === 'month')
    return Math.min(10, Math.max(3, Math.ceil(spanDaysVal / 3)));
  if (scale === 'months') {
    const months = diffInMonths(minTs, maxTs) + 1;
    return Math.min(12, Math.max(3, months));
  }
  // years
  const years = diffInYears(minTs, maxTs) + 1;
  return Math.min(10, Math.max(2, years));
};

const generateColors = (optionArray: Option[]): string[] => {
  return optionArray.length > 0
    ? optionArray.map((s, index) =>
        getStaticColorForEntity(String(s.value), index),
      )
    : [getStaticColorForEntity('no-data', 0)];
};

const createBaseChartOptions = (
  minTs: number,
  maxTs: number,
  tickAmount: number,
  colors: string[],
  events: any = {},
  locale = 'fr',
  yLabel = '',
): ApexOptions => {
  const localesConfig = getApexchartsLocales(locale);
  const options: ApexOptions = {
    chart: {
      type: 'line',
      ...localesConfig,
      toolbar: {
        show: true,
        tools: {
          download: false,
          pan: true,
          reset: true,
          selection: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
        },
      },
      zoom: {
        enabled: true,
        type: 'x',
        autoScaleYaxis: true,
        allowMouseWheelZoom: false,
      },
      events,
    },
    colors,
    stroke: { curve: 'straight', width: 3 },
    markers: { size: 0 },
    xaxis: {
      type: 'datetime',
      min: minTs,
      max: maxTs,
      tickPlacement: 'on',
      tickAmount,
      tooltip: { enabled: false },
      labels: {
        show: true,
        datetimeUTC: true,
        showDuplicates: true,
        hideOverlappingLabels: true,
        datetimeFormatter: {
          year: 'yyyy',
          month: 'dd MMM',
          day: 'dd MMM',
          hour: '',
          minute: '',
          second: '',
        },
      },
    },
    yaxis: createYAxisConfig(10, yLabel),
    tooltip: {
      shared: true,
      intersect: false,
      followCursor: false,
      custom: createCustomTooltip(),
    },
    legend: { show: false },
    grid: {
      yaxis: { lines: { show: true } },
      xaxis: { lines: { show: false } },
      row: { colors: undefined },
      column: { colors: undefined },
    },
  };

  return options;
};

const createSimpleChartOptions = (locale = 'fr'): ApexOptions => {
  const localesConfig = getApexchartsLocales(locale);
  return {
    chart: {
      type: 'line',
      ...localesConfig,
    },
    xaxis: {
      type: 'datetime',
      labels: {
        datetimeUTC: true,
      },
    },
    yaxis: createYAxisConfig(),
    legend: { show: false },
    grid: {
      yaxis: { lines: { show: true } },
      xaxis: { lines: { show: false } },
      row: { colors: undefined },
      column: { colors: undefined },
    },
  };
};

export default function prepareAnomaliesChart({
  input,
  optionArray,
  dateRange,
  locale,
  yLabel,
}: {
  input: Array<{
    id?: string;
    period: string; // YYYY-MM-DD
    totalAnomalies: number;
  }>;
  optionArray: Option[];
  dateRange?: { min?: string; max?: string };
  locale?: string;
  yLabel: string;
}): AnomaliesChartData {
  // Collect unique dates and per-entity per-date values
  const perEntity = new Map<string, Map<string, number>>();
  const dateSet = new Set<string>();

  input.forEach(({ id, period, totalAnomalies }) => {
    if (!id) return;
    const key = String(id);
    if (!perEntity.has(key)) perEntity.set(key, new Map());
    perEntity.get(key)?.set(period, totalAnomalies ?? 0);
    dateSet.add(period);
  });

  // Ensure all entities from the filter are present, even if they have no data
  optionArray.forEach((s) => {
    const key = String(s.value);
    if (!perEntity.has(key)) perEntity.set(key, new Map());
  });

  const sortedInputDates = Array.from(dateSet).sort((a, b) =>
    a.localeCompare(b),
  );

  // Use CET timezone for "today" date instead of UTC
  const todayYmd = getFormattedDate(new Date(), dateFormatForApi) as string;
  const sixDaysAgoYmd = getFormattedDate(
    new Date(Date.now() - 6 * DAY_MS),
    dateFormatForApi,
  ) as string;

  const start = sortedInputDates[0] || dateRange?.min || sixDaysAgoYmd;
  const end =
    sortedInputDates[sortedInputDates.length - 1] || dateRange?.max || todayYmd;

  if (!start || !end) {
    return { options: createSimpleChartOptions(), series: [] };
  }

  const startTsRaw = ymdToUtcTs(start);
  const endTsRaw = toUtcEndOfDay(end);

  const filterMinTs = dateRange?.min
    ? toUtcStartOfDay(dateRange.min)
    : undefined;
  const filterMaxTs = dateRange?.max ? toUtcEndOfDay(dateRange.max) : undefined;

  // If filter provided, use it as the visible window; otherwise fallback to data span
  const startTs = typeof filterMinTs === 'number' ? filterMinTs : startTsRaw;
  const endTs = typeof filterMaxTs === 'number' ? filterMaxTs : endTsRaw;

  // Visible range (will be padded for very short spans)
  const adjustedStartTs = startTs;
  const adjustedEndTs = endTs;

  // If the filter cuts everything off, return an empty chart
  if (adjustedEndTs < adjustedStartTs) {
    const options = createSimpleChartOptions();
    options.xaxis = { ...options.xaxis, min: filterMinTs, max: filterMaxTs };
    return { options, series: [] };
  }

  const totalDays = Math.floor((adjustedEndTs - adjustedStartTs) / DAY_MS) + 1;

  const allDates: string[] = Array.from({ length: totalDays }, (_, i) => {
    const d = new Date(adjustedStartTs + i * DAY_MS);
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(d.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
  });

  // Keep dates strictly UTC-based to avoid timezone inconsistencies

  const isOneDayRange = totalDays === 1;

  // Build series for each entity, guaranteeing data points for every date
  const series: AxisSeries =
    optionArray.length > 0
      ? optionArray.map((s) => {
          const entityId = String(s.value);
          const entityName =
            typeof s.label === 'string' ? s.label : String(s.value);
          const dateMap = perEntity.get(entityId);
          // When no data is present, dateMap will be an empty map.
          // We create a full series of zero-data points.
          if (!dateMap || dateSet.size === 0) {
            return {
              name: entityName,
              data: allDates.map((d) => ({
                x: ymdToUtcTs(d) + (isOneDayRange ? DAY_MS / 2 : 0),
                y: 0,
              })),
            };
          }

          return {
            name: entityName,
            data: allDates.map((d) => ({
              x: ymdToUtcTs(d) + (isOneDayRange ? DAY_MS / 2 : 0),
              y: dateMap.has(d) ? (dateMap.get(d) ?? 0) : 0,
            })),
          };
        })
      : [
          {
            name: 'No data',
            data: allDates.map((d) => ({
              x: ymdToUtcTs(d) + (isOneDayRange ? DAY_MS / 2 : 0),
              y: 0,
            })),
          },
        ];

  const days = allDates.length;

  // Use shared scaling logic
  const xScale = chooseScale(days);
  const desiredTickAmount = getTickAmount(
    xScale,
    days,
    adjustedStartTs,
    adjustedEndTs,
  );
  const colors = generateColors(optionArray);

  const MIN_ZOOM_SPAN_MS = 7 * DAY_MS;
  const totalSpan = adjustedEndTs - adjustedStartTs;

  // Viewport boundaries
  // - 1 day: pad ±12h to keep the point centered
  // - >=2 days: add a tiny pad on both edges so Apex does not hide edge labels
  const PAD_HALF_DAY = DAY_MS / 2;
  const EDGE_EPS = 60 * 60 * 1000; // 1 hour padding to keep the last label visible
  const firstDayStart = ymdToUtcTs(allDates[0]);
  const lastDayStart = ymdToUtcTs(allDates[allDates.length - 1]);
  const viewMin =
    totalDays === 1 ? adjustedStartTs - PAD_HALF_DAY : firstDayStart - EDGE_EPS;
  const viewMax =
    totalDays === 1 ? adjustedEndTs + PAD_HALF_DAY : lastDayStart + EDGE_EPS;

  const chartEvents = {
    beforeZoom: (_chartContext: any, { xaxis }: any) => {
      const newMin = xaxis.min;
      const newMax = xaxis.max;

      if (totalSpan < MIN_ZOOM_SPAN_MS) {
        return { xaxis: { min: viewMin, max: viewMax } };
      }

      if (newMax - newMin < MIN_ZOOM_SPAN_MS) {
        const mid = (newMin + newMax) / 2;
        let finalMin = mid - MIN_ZOOM_SPAN_MS / 2;
        let finalMax = mid + MIN_ZOOM_SPAN_MS / 2;

        if (finalMin < viewMin) {
          finalMin = viewMin;
          finalMax = viewMin + MIN_ZOOM_SPAN_MS;
        }
        if (finalMax > viewMax) {
          finalMax = viewMax;
          finalMin = viewMax - MIN_ZOOM_SPAN_MS;
        }

        return { xaxis: { min: finalMin, max: finalMax } };
      }

      return { xaxis: { min: newMin, max: newMax } };
    },
    beforeResetZoom: () => {
      return { xaxis: { min: viewMin, max: viewMax } };
    },
  };

  const options = createBaseChartOptions(
    viewMin, // min (possibly padded)
    viewMax, // max (possibly padded)
    desiredTickAmount,
    colors,
    chartEvents,
    locale,
    yLabel,
  );

  // Single-day: ensure only the selected day label is rendered (and formatted as date)
  if (days === 1 && options.xaxis) {
    const dayStart = toUtcStartOfDay(allDates[0]);
    const dayEnd = toUtcEndOfDay(allDates[0]);
    const loc = locale || 'fr';
    options.xaxis = {
      ...options.xaxis,
      labels: {
        ...(options.xaxis.labels || {}),
        formatter: (_val: string, ts?: number) => {
          const timestamp = typeof ts === 'number' ? ts : undefined;
          // Apex sometimes calls with value string; rely on tick timestamp if present
          const inside =
            typeof timestamp === 'number' &&
            timestamp >= dayStart &&
            timestamp <= dayEnd;
          if (!inside) return '';
          try {
            return new Date(timestamp).toLocaleDateString(loc, {
              day: '2-digit',
              month: 'short',
            });
          } catch {
            return '';
          }
        },
      },
    };
  }

  return { options, series };
}
